package fr.frux.feature.treedetail.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Restaurant
import androidx.compose.material.icons.outlined.Terrain
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.feature.treedetail.resources.Res
import fr.frux.feature.treedetail.resources.treedetail_trait_access_difficult
import fr.frux.feature.treedetail.resources.treedetail_trait_delicious
import fr.frux.feature.treedetail.resources.treedetail_trait_description_mine_suffix
import fr.frux.feature.treedetail.resources.treedetail_trait_description_plural
import fr.frux.feature.treedetail.resources.treedetail_trait_description_singular
import fr.frux.shared.domain.model.TreeTrait
import fr.frux.shared.domain.model.TreeTraitKind
import org.jetbrains.compose.resources.stringResource

/** Icône vectorielle associée à chaque kind de trait (jamais d'emoji, tofu js). */
private fun TreeTraitKind.icon(): ImageVector =
    when (this) {
        TreeTraitKind.ACCESS_DIFFICULT -> Icons.Outlined.Terrain
        TreeTraitKind.DELICIOUS -> Icons.Outlined.Restaurant
    }

/** Libellé français du trait, pour l'affichage et le contentDescription (a11y). */
@Composable
private fun TreeTraitKind.label(): String =
    when (this) {
        TreeTraitKind.ACCESS_DIFFICULT -> stringResource(Res.string.treedetail_trait_access_difficult)
        TreeTraitKind.DELICIOUS -> stringResource(Res.string.treedetail_trait_delicious)
    }

/**
 * Ligne de chips de traits (ADR 0021, restylée par la spec Fiche arbre) : pill 48 dp, icône +
 * libellé + compteur. Un trait posé par l'appelant s'affiche en `primaryContainer` avec un anneau
 * `primary` ; le tap le retire (togglable, cf. TreeRepository.removeTreeTrait).
 *
 * @param pendingTraits Kinds dont la bascule (ajout/retrait) est en cours d'appel — chip à 50 %
 * d'opacité et désactivé le temps de l'appel, sans lien avec [TreeTrait.mine].
 */
@Composable
fun TraitChipRow(
    traits: List<TreeTrait>,
    pendingTraits: Set<TreeTraitKind>,
    onTraitClick: (TreeTraitKind) -> Unit,
    modifier: Modifier = Modifier,
) {
    Row(
        modifier = modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
    ) {
        traits.forEach { trait ->
            TraitChip(
                trait = trait,
                pending = trait.kind in pendingTraits,
                onClick = { onTraitClick(trait.kind) },
            )
        }
    }
}

@Composable
private fun TraitChip(
    trait: TreeTrait,
    pending: Boolean,
    onClick: () -> Unit,
) {
    val label = trait.kind.label()
    val contentColor =
        if (trait.mine) {
            MaterialTheme.colorScheme.onPrimaryContainer
        } else {
            MaterialTheme.colorScheme.onSurfaceVariant
        }
    val base =
        if (trait.count == 1) {
            stringResource(Res.string.treedetail_trait_description_singular, label, trait.count)
        } else {
            stringResource(Res.string.treedetail_trait_description_plural, label, trait.count)
        }
    val description = base + if (trait.mine) stringResource(Res.string.treedetail_trait_description_mine_suffix) else ""
    Surface(
        onClick = onClick,
        enabled = !pending,
        modifier =
            Modifier
                .height(48.dp)
                .alpha(if (pending) 0.5f else 1f)
                .semantics { contentDescription = description },
        shape = RoundedCornerShape(50),
        color =
            if (trait.mine) {
                MaterialTheme.colorScheme.primaryContainer
            } else {
                MaterialTheme.colorScheme.surfaceVariant
            },
        contentColor = contentColor,
        border = if (trait.mine) BorderStroke(1.dp, MaterialTheme.colorScheme.primary) else null,
    ) {
        Row(
            modifier =
                Modifier
                    .fillMaxHeight()
                    .padding(horizontal = 14.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            Icon(
                imageVector = trait.kind.icon(),
                contentDescription = null,
                modifier = Modifier.size(20.dp),
                tint = contentColor,
            )
            Text(
                text = label,
                style = MaterialTheme.typography.labelMedium,
                color = contentColor,
            )
            if (trait.count > 0) {
                Text(
                    text = trait.count.toString(),
                    style = MaterialTheme.typography.bodySmall,
                    color = contentColor,
                )
            }
        }
    }
}
